<template>
  <v-main>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              :title="isDeletePage() != true ? 'Order Items Management' : 'Deleted Order Items'"
              :subtitle="isDeletePage() != true ? 'Manage and monitor all order items with their details' : 'Restore or permanently delete order items'"
            >
              <template v-slot:actions>
                <div v-if="isDeletePage() != true" class="d-flex justify-end align-center">
                    <v-btn
                      color="primary"
                      elevation="1"
                      small
                      to="/order-items/create"
                      class="mr-2 px-3"
                    >
                      <v-icon left x-small>mdi-plus</v-icon>
                      Add Order Item
                    </v-btn>
                  </div>
              </template>
            </page-header>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-filter-variant</v-icon
                    >
                    <span class="text-body-2 font-weight-medium"
                      >Filter Order Items</span
                    >
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <v-row>
                <!-- Search Field -->
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="filter.search_keyword"
                    label="Search Order Items"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    placeholder="Order, product, quantity..."
                    hide-details
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>

                <!-- Order Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.order_id"
                    :items="orders"
                    item-text="text"
                    item-value="value"
                    label="Order"
                    prepend-inner-icon="mdi-package-variant"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Product Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.product_id"
                    :items="products"
                    item-text="text"
                    item-value="value"
                    label="Product"
                    prepend-inner-icon="mdi-cube-outline"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Status Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.is_active"
                    :items="statuses"
                    item-text="text"
                    item-value="value"
                    label="Status"
                    prepend-inner-icon="mdi-toggle-switch"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Combined Order Items Table Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Card Header with Title and Controls -->
            <v-card-title class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Title Section -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-table</v-icon
                    >
                    <span class="text-body-1 font-weight-medium"
                      >Order Items List</span
                    >
                  </div>
                </v-col>

                <!-- Results Summary Section -->
                <v-col cols="12" md="5" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-chip color="primary" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-package-variant</v-icon>
                      {{ count }} {{ count === 1 ? "Item" : "Items" }}
                    </v-chip>
                    <span class="text-caption grey--text" v-if="count > 0">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Controls Section -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Items per page selector -->
                    <div class="d-flex align-center mr-2">
                      <span class="text-caption grey--text mr-1">Show:</span>
                      <v-btn-toggle
                        v-model="selectedItemsPerPage"
                        dense
                        class="mr-2"
                        @change="changeItemsPerPageToggle"
                      >
                        <v-btn x-small outlined :value="10">10</v-btn>
                        <v-btn x-small outlined :value="25">25</v-btn>
                        <v-btn x-small outlined :value="50">50</v-btn>
                      </v-btn-toggle>
                    </div>

                    <!-- Export Button -->
                    <v-btn
                      v-if="!isDeletePage() && orderItems.length > 0"
                      x-small
                      color="success"
                      outlined
                      @click="generateExcel"
                      class="px-2"
                      :loading="isExcelloading"
                    >
                      <v-icon left x-small>mdi-microsoft-excel</v-icon>
                      Export
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <!-- Table Content -->
            <v-card-text class="pa-0">
              <!-- Loading State -->
              <section
                v-if="isLoading"
                style="height: 400px"
                class="d-flex align-center justify-center"
              >
                <div class="text-center">
                  <health-loader message="Loading order items..." />
                </div>
              </section>

              <!-- Data Table -->
              <v-data-table
                v-else
                :headers="headers"
                :items="orderItems"
                :server-items-length="count"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                class="enhanced-table"
              >
                <!-- Serial Number Column -->
                <template #[`item.sr_no`]="{ index }">
                  <span class="font-weight-medium primary--text">
                    {{ (page - 1) * itemsPerPage + index + 1 }}
                  </span>
                </template>

                <!-- Status Column -->
                <template #[`item.is_active`]="{ item }">
                  <div class="status-container">
                    <v-chip
                      :color="item.is_active ? 'success' : 'error'"
                      small
                      :outlined="!item.is_active"
                      class="status-chip"
                    >
                      <v-icon left x-small>
                        {{
                          item.is_active
                            ? "mdi-check-circle"
                            : "mdi-pause-circle"
                        }}
                      </v-icon>
                      {{ item.is_active ? "Active" : "Inactive" }}
                    </v-chip>
                  </div>
                </template>

                <!-- Order Info Column -->
                <template #[`item.order_info`]="{ item }">
                  <div class="d-flex align-center order-info-container">
                    <v-avatar
                      size="36"
                      class="mr-3"
                      :color="getOrderStatusColor(item.order)"
                    >
                      <v-icon color="white" size="18">mdi-receipt</v-icon>
                    </v-avatar>
                    <div class="order-details">
                      <div class="text-body-2 font-weight-medium">
                        {{ item.order ? item.order.order_number : "No order" }}
                      </div>
                      <div class="text-caption grey--text d-flex align-center">
                        <v-icon size="12" color="info" class="mr-1"
                          >mdi-identifier</v-icon
                        >
                        ID: {{ item.order ? item.order.id : "N/A" }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Product Info Column -->
                <template #[`item.product_info`]="{ item }">
                  <div class="d-flex align-center product-info-container">
                    <v-avatar size="40" class="mr-3 product-avatar">
                      <v-img
                        :src="
                          item.product && item.product.image_path
                            ? item.product.image_path
                            : '/images/default-product.png'
                        "
                        :alt="item.product ? item.product.name : ''"
                      />
                    </v-avatar>
                    <div class="product-details">
                      <div class="text-body-2 font-weight-medium primary--text">
                        {{
                          truncateText(
                            item.product ? item.product.name : "No product",
                            25
                          )
                        }}
                      </div>
                      <div class="text-caption grey--text d-flex align-center">
                        <v-icon size="12" color="success" class="mr-1"
                          >mdi-currency-inr</v-icon
                        >
                        ${{ parseFloat(item.product_price).toFixed(2) }} each
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Quantity Column -->
                <template #[`item.quantity`]="{ item }">
                  <div class="quantity-container">
                    <v-chip
                      small
                      :color="getQuantityColor(item.quantity)"
                      :text-color="getQuantityTextColor(item.quantity)"
                      class="quantity-chip"
                    >
                      <v-icon left x-small>{{
                        getQuantityIcon(item.quantity)
                      }}</v-icon>
                      {{ item.quantity }}
                    </v-chip>
                    <div class="text-caption grey--text text-center mt-1">
                      {{ item.quantity === 1 ? "unit" : "units" }}
                    </div>
                  </div>
                </template>

                <!-- Total Price Column -->
                <template #[`item.total_price`]="{ item }">
                  <div class="price-container">
                    <div class="text-body-2 font-weight-bold success--text">
                      ${{ parseFloat(item.total_price).toFixed(2) }}
                    </div>
                    <div class="text-caption grey--text">Total Value</div>
                  </div>
                </template>

                <!-- Created Date Column -->
                <template #[`item.created_at`]="{ item }">
                  <div class="date-container">
                    <div class="text-caption font-weight-medium">
                      {{ formatDate(item.created_at) }}
                    </div>
                    <div class="text-caption grey--text">
                      {{ getTimeAgo(item.created_at) }}
                    </div>
                  </div>
                </template>

                <!-- Actions Column -->
                <template #[`item.actions`]="{ item }">
                  <div class="d-flex justify-center actions-container">
                    <template v-if="!isDeletePage()">
                      <!-- View Details Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="info"
                            outlined
                            class="mr-1 action-btn"
                            @click="viewItemDetails(item)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-eye</v-icon>
                          </v-btn>
                        </template>
                        <span>View Details</span>
                      </v-tooltip>

                      <!-- Edit Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="primary"
                            :to="`/order-items/${encryptIt(item.id)}`"
                            class="mr-1 action-btn"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-pencil</v-icon>
                          </v-btn>
                        </template>
                        <span>Edit Order Item</span>
                      </v-tooltip>

                      <!-- Delete Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="error"
                            outlined
                            @click="showDeleteConfirmation(item)"
                            class="action-btn"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-delete</v-icon>
                          </v-btn>
                        </template>
                        <span>Delete Order Item</span>
                      </v-tooltip>
                    </template>

                    <template v-else>
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            small
                            color="success"
                            @click="Restore_this(item.id)"
                            class="action-btn"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon left x-small>mdi-restore</v-icon>
                            Restore
                          </v-btn>
                        </template>
                        <span>Restore Order Item</span>
                      </v-tooltip>
                    </template>
                  </div>
                </template>

                <!-- Empty State -->
                <template #no-data>
                  <div class="text-center py-12">
                    <v-icon size="80" color="grey lighten-3"
                      >mdi-package-variant-closed</v-icon
                    >
                    <h3 class="text-h6 grey--text mt-4 mb-2">
                      No Order Items Found
                    </h3>
                    <p class="grey--text mb-4">
                      {{
                        hasActiveFilters
                          ? "No order items match your current filters. Try adjusting your search criteria."
                          : "You haven't added any order items yet."
                      }}
                    </p>
                    <v-btn
                      v-if="!hasActiveFilters && !isDeletePage()"
                      color="primary"
                      to="/order-items/create"
                    >
                      <v-icon left>mdi-plus</v-icon>
                      Add Your First Order Item
                    </v-btn>
                    <v-btn
                      v-else-if="hasActiveFilters"
                      color="primary"
                      outlined
                      @click="clearAllFilters"
                    >
                      <v-icon left>mdi-filter-remove</v-icon>
                      Clear Filters
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Integrated Pagination Footer -->
            <v-divider></v-divider>
            <v-card-actions class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Pagination Info -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <span class="text-caption grey--text">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Pagination Controls -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <!-- Previous Button -->
                    <v-btn
                      x-small
                      outlined
                      :disabled="page <= 1"
                      @click="page--"
                      class="mr-2"
                    >
                      <v-icon x-small left>mdi-chevron-left</v-icon>
                      Prev
                    </v-btn>

                    <!-- Page Numbers -->
                    <v-pagination
                      v-model="page"
                      :length="paginationLength"
                      :total-visible="5"
                      class="compact-pagination mx-1"
                    ></v-pagination>

                    <!-- Next Button -->
                    <v-btn
                      x-small
                      outlined
                      :disabled="page >= paginationLength"
                      @click="page++"
                      class="ml-2"
                    >
                      Next
                      <v-icon x-small right>mdi-chevron-right</v-icon>
                    </v-btn>
                  </div>
                </v-col>

                <!-- Jump to Page -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <span class="text-caption grey--text mr-2">Go to:</span>
                    <v-text-field
                      v-model="jumpToPage"
                      type="number"
                      :min="1"
                      :max="paginationLength"
                      dense
                      outlined
                      hide-details
                      hide-spin-buttons
                      class="no-spin-arrows compact-input"
                      style="max-width: 60px"
                      @keydown.enter="goToPage"
                      @blur="goToPage"
                    ></v-text-field>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Item Details Dialog -->
      <v-dialog v-model="showDetailsDialog" max-width="600">
        <v-card v-if="selectedItem">
          <v-card-title class="primary white--text d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-information-outline</v-icon>
            Order Item Details
            <v-spacer></v-spacer>
            <v-btn icon @click="showDetailsDialog = false">
              <v-icon color="white">mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="pt-4">
            <v-row>
              <v-col cols="12" md="6">
                <div class="detail-section">
                  <h4 class="text-subtitle-1 font-weight-bold mb-2">
                    Order Information
                  </h4>
                  <div class="detail-item">
                    <span class="detail-label">Order Number:</span>
                    <span class="detail-value">{{
                      selectedItem.order
                        ? selectedItem.order.order_number
                        : "N/A"
                    }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Order ID:</span>
                    <span class="detail-value">{{
                      selectedItem.order ? selectedItem.order.id : "N/A"
                    }}</span>
                  </div>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="detail-section">
                  <h4 class="text-subtitle-1 font-weight-bold mb-2">
                    Product Information
                  </h4>
                  <div class="detail-item">
                    <span class="detail-label">Product Name:</span>
                    <span class="detail-value">{{
                      selectedItem.product ? selectedItem.product.name : "N/A"
                    }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Unit Price:</span>
                    <span class="detail-value"
                      >${{
                        parseFloat(selectedItem.product_price).toFixed(2)
                      }}</span
                    >
                  </div>
                </div>
              </v-col>

              <v-col cols="12">
                <div class="detail-section">
                  <h4 class="text-subtitle-1 font-weight-bold mb-2">
                    Item Details
                  </h4>
                  <v-row>
                    <v-col cols="6" md="3">
                      <div class="stat-card">
                        <div class="stat-value">
                          {{ selectedItem.quantity }}
                        </div>
                        <div class="stat-label">Quantity</div>
                      </div>
                    </v-col>
                    <v-col cols="6" md="3">
                      <div class="stat-card">
                        <div class="stat-value">
                          ${{ parseFloat(selectedItem.total_price).toFixed(2) }}
                        </div>
                        <div class="stat-label">Total Value</div>
                      </div>
                    </v-col>
                    <v-col cols="6" md="3">
                      <div class="stat-card">
                        <div class="stat-value">
                          {{ selectedItem.is_active ? "Active" : "Inactive" }}
                        </div>
                        <div class="stat-label">Status</div>
                      </div>
                    </v-col>
                    <v-col cols="6" md="3">
                      <div class="stat-card">
                        <div class="stat-value">
                          {{ formatDate(selectedItem.created_at) }}
                        </div>
                        <div class="stat-label">Date Added</div>
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="showDeleteDialog" max-width="500" persistent>
        <v-card>
          <v-card-title class="headline error white--text d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-alert-circle</v-icon>
            Confirm Delete
          </v-card-title>

          <v-card-text class="pt-4">
            <div class="text-center">
              <v-icon size="64" color="error" class="mb-3"
                >mdi-delete-alert</v-icon
              >
              <h3 class="mb-3">
                Are you sure you want to delete this order item?
              </h3>

              <div
                class="item-info-card pa-3 ma-2"
                style="background: #f5f5f5; border-radius: 8px"
              >
                <div class="d-flex align-center">
                  <v-avatar size="48" class="mr-3">
                    <v-img
                      :src="
                        itemToDelete &&
                        itemToDelete.product &&
                        itemToDelete.product.image_path
                          ? itemToDelete.product.image_path
                          : '/images/default-product.png'
                      "
                      :alt="
                        itemToDelete && itemToDelete.product
                          ? itemToDelete.product.name
                          : ''
                      "
                    />
                  </v-avatar>
                  <div class="text-left">
                    <div class="font-weight-bold text-body-1">
                      {{
                        itemToDelete && itemToDelete.product
                          ? itemToDelete.product.name
                          : "Unknown Product"
                      }}
                    </div>
                    <div class="text-caption grey--text">
                      Order:
                      {{
                        itemToDelete && itemToDelete.order
                          ? itemToDelete.order.order_number
                          : "N/A"
                      }}
                    </div>
                    <div class="text-caption grey--text">
                      Quantity: {{ itemToDelete ? itemToDelete.quantity : 0 }} |
                      Total: ${{
                        itemToDelete
                          ? parseFloat(itemToDelete.total_price).toFixed(2)
                          : "0.00"
                      }}
                    </div>
                  </div>
                </div>
              </div>

              <p class="text-body-2 grey--text mt-3 mb-1">
                <strong>Warning:</strong> This action will move the order item
                to trash.
              </p>
              <p class="text-body-2 grey--text">
                You can restore it later from the deleted items section.
              </p>
            </div>
          </v-card-text>

          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn
              color="grey darken-1"
              text
              @click="cancelDelete"
              :disabled="isDeleting"
            >
              <v-icon left>mdi-close</v-icon>
              Cancel
            </v-btn>
            <v-btn
              color="error"
              @click="confirmDelete"
              :loading="isDeleting"
              :disabled="isDeleting"
            >
              <v-icon left>mdi-delete</v-icon>
              Delete Item
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <DeleteConfirmationDialog
        v-model="deleteDialog"
        :item="itemToDelete"
        :loading="isDeleting"
        @confirm="confirmDelete"
        @cancel="deleteDialog = false"
      />

      <!-- Restore Success Dialog Box -->
      <v-dialog v-model="is_restored_order_item" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-restore
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Hurray!</h1>
                      <br />
                      <h3>Order item restored successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="is_restored_order_item = false"
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog.vue";

export default {
  name: "OrderItemsIndex",
  components: { DeleteConfirmationDialog },
  data() {
    return {
      filter: {
        search_keyword: "",
        order_id: "",
        product_id: "",
        is_active: "",
        show_deleted: false,
      },
      page: 1,
      serialNoStarting: 0,
      paginationLength: 0,
      count: 0,
      is_deleted_order_item: false,
      is_restored_order_item: false,
      orderItems: [],
      orders: [
        {
          id: 0,
          text: "Select Order",
          value: "",
        },
      ],
      products: [
        {
          id: 0,
          text: "Select Product",
          value: "",
        },
      ],
      statuses: [
        { text: "Active", value: "1" },
        { text: "Inactive", value: "0" },
      ],
      itemsPerPage: 10,
      selectedItemsPerPage: 10,
      jumpToPage: 1,
      headers: [
        {
          text: "#",
          value: "sr_no",
          sortable: false,
          width: "60px",
          align: "center",
        },
        {
          text: "Order Information",
          value: "order_info",
          sortable: false,
          width: "200px",
        },
        {
          text: "Product Details",
          value: "product_info",
          sortable: false,
          width: "220px",
        },
        {
          text: "Quantity",
          value: "quantity",
          sortable: false,
          width: "110px",
          align: "center",
        },
        {
          text: "Total Value",
          value: "total_price",
          sortable: false,
          width: "120px",
        },
        {
          text: "Date Added",
          value: "created_at",
          sortable: false,
          width: "140px",
        },
        {
          text: "Status",
          value: "is_active",
          sortable: false,
          width: "90px",
          align: "center",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          width: "140px",
          align: "center",
        },
      ],
      showDetailsDialog: false,
      selectedItem: null,
      showDeleteDialog: false,
      deleteDialog: false,
      itemToDelete: null,
      isDeleting: false,
      isLoading: false,
      isExcelloading: false,
    };
  },
  computed: {
    hasActiveFilters() {
      return (
        this.filter.search_keyword ||
        this.filter.order_id ||
        this.filter.product_id ||
        this.filter.is_active
      );
    },
  },

  watch: {
    page: {
      handler: function (newPage) {
        this.jumpToPage = newPage;
        this.getData();
      },
    },
    itemsPerPage: "getData",
    $route() {
      this.getData();
    },
  },
  mounted() {
    this.jumpToPage = this.page;
    this.getData();
    this.getOrders();
    this.getProducts();
  },
  methods: {
    async getOrders() {
      try {
        const response = await axios.get("/order_items/orders");
        const orders = response.data.data;

        this.orders = [{ id: 0, text: "Select Order", value: "" }];
        orders.forEach((order) => {
          this.orders.push({
            id: order.id,
            text: order.order_number,
            value: order.id,
          });
        });
      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      }
    },
    async getProducts() {
      try {
        const response = await axios.get("/order_items/products");
        const products = response.data.data;

        this.products = [{ id: 0, text: "Select Product", value: "" }];
        products.forEach((product) => {
          this.products.push({
            id: product.id,
            text: product.name,
            value: product.id,
          });
        });
      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      }
    },
    async getData() {
      this.filter.show_deleted = this.$route.query.open_trash ? 1 : 0;
      this.isLoading = true;

      this.filter.search_keyword = this.filter.search_keyword || "";
      this.filter.order_id = this.filter.order_id || "";
      this.filter.product_id = this.filter.product_id || "";
      this.filter.is_active = this.filter.is_active || "";

      let query =
        "page=" +
        this.page +
        "&rowsPerPage=" +
        this.itemsPerPage +
        "&show_deleted=" +
        this.filter.show_deleted +
        "&is_active=" +
        this.filter.is_active +
        "&order_id=" +
        this.filter.order_id +
        "&product_id=" +
        this.filter.product_id +
        "&search_keyword=" +
        this.filter.search_keyword;

      try {
        const response = await axios.get(`order_items?${query}`);
        this.orderItems = response.data.data;
        this.count = response.data.count;
        this.serialNoStarting = (this.page - 1) * this.itemsPerPage;
        this.paginationLength = Math.ceil(this.count / this.itemsPerPage);
      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      }

      this.isLoading = false;
    },
    async Delete_this(id) {
      try {
        await axios.post(`/order_items/delete/${this.encryptIt(id)}`);
        this.is_deleted_order_item = true;
        this.getData();
      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      }
    },
    async Restore_this(id) {
      try {
        await axios.post(`/order_items/restore/${this.encryptIt(id)}`);
        this.is_restored_order_item = true;
        this.getData();
      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      }
    },
    async generateExcel() {
      this.isExcelloading = true;

      this.filter.search_keyword = this.filter.search_keyword || "";
      this.filter.order_id = this.filter.order_id || "";
      this.filter.product_id = this.filter.product_id || "";
      this.filter.is_active = this.filter.is_active || "";

      let query =
        "show_deleted=" +
        this.filter.show_deleted +
        "&is_active=" +
        this.filter.is_active +
        "&order_id=" +
        this.filter.order_id +
        "&product_id=" +
        this.filter.product_id +
        "&search_keyword=" +
        this.filter.search_keyword;

      try {
        const response = await axios.get(`/order_items/excel?${query}`, {
          responseType: "blob",
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;

        const contentDisposition = response.headers["content-disposition"];
        let filename = "order_items_export.xlsx";
        if (contentDisposition) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, "");
          }
        }

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Excel export failed:", error);
      }

      this.isExcelloading = false;
    },
    clearAllFilters() {
      this.filter.search_keyword = "";
      this.filter.order_id = "";
      this.filter.product_id = "";
      this.filter.is_active = "";
      this.page = 1;
      this.getData();
    },

    getActiveFiltersCount() {
      let count = 0;
      if (this.filter.search_keyword) count++;
      if (this.filter.order_id) count++;
      if (this.filter.product_id) count++;
      if (this.filter.is_active) count++;
      return count;
    },

    changeItemsPerPageToggle() {
      this.itemsPerPage = this.selectedItemsPerPage;
      this.page = 1;
      this.getData();
    },

    goToPage() {
      const targetPage = parseInt(this.jumpToPage);
      if (
        targetPage &&
        targetPage >= 1 &&
        targetPage <= this.paginationLength
      ) {
        this.page = targetPage;
      } else {
        // Reset to current page if invalid input
        this.jumpToPage = this.page;
      }
    },

    isDeletePage() {
      return this.$route.query.open_trash ? true : false;
    },
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    getTimeAgo(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 1) return "1 day ago";
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
      return `${Math.ceil(diffDays / 30)} months ago`;
    },

    truncateText(text, maxLength) {
      if (!text) return "";
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + "...";
    },

    getQuantityColor(quantity) {
      if (quantity >= 20) return "success";
      if (quantity >= 10) return "warning";
      if (quantity >= 5) return "orange";
      return "error";
    },

    getQuantityTextColor() {
      return "white";
    },

    getQuantityIcon(quantity) {
      if (quantity >= 20) return "mdi-check-circle";
      if (quantity >= 10) return "mdi-alert-circle";
      if (quantity >= 5) return "mdi-minus-circle";
      return "mdi-alert-circle-outline";
    },

    getOrderStatusColor(order) {
      if (!order) return "grey";
      // You can add order status logic here based on order properties
      return "primary";
    },

    viewItemDetails(item) {
      this.selectedItem = item;
      this.showDetailsDialog = true;
    },

    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.showDeleteDialog = true;
    },

    openDeleteDialog(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },

    cancelDelete() {
      this.showDeleteDialog = false;
      this.itemToDelete = null;
      this.isDeleting = false;
    },

    async confirmDelete() {
      if (!this.itemToDelete) return;

      this.isDeleting = true;

      try {
        await axios.post(
          `/order_items/delete/${this.encryptIt(this.itemToDelete.id)}`
        );
        this.showDeleteDialog = false;
        this.itemToDelete = null;
        this.isDeleting = false;
        this.is_deleted_order_item = true;
        this.getData();
      } catch (error) {
        console.log(error);
        this.isDeleting = false;
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      }
    },
  },
};
</script>

<style scoped>
/* Compact Layout Adjustments */
.v-container {
  max-width: 100% !important;
}

/* Enhanced Table Styling - Compact Version */
.enhanced-table >>> .v-data-table__wrapper {
  border-radius: 8px;
  font-size: 0.75rem !important;
}

.enhanced-table >>> .v-data-table-header {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  font-weight: 600;
}

.enhanced-table >>> .v-data-table-header th {
  padding: 8px 6px !important;
  border-bottom: 1px solid #e0e0e0 !important;
  font-size: 0.75rem !important;
  height: 36px !important;
}

.enhanced-table >>> tbody tr:hover {
  background: rgba(25, 118, 210, 0.04) !important;
  transition: all 0.1s ease;
}

.enhanced-table >>> tbody tr {
  height: 44px !important;
  transition: all 0.1s ease;
}

.enhanced-table >>> tbody td {
  padding: 6px 6px !important;
  vertical-align: middle !important;
  border-bottom: 1px solid #f5f5f5 !important;
  font-size: 0.75rem !important;
  height: 44px !important;
}

/* Compact Filter Cards */
.v-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95);
}

.v-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

/* Compact Button Styling */
.v-btn {
  text-transform: none !important;
  font-weight: 500;
  letter-spacing: 0.2px;
  font-size: 0.75rem !important;
  min-height: 28px !important;
}

.v-btn--x-small {
  height: 24px !important;
  min-width: 48px !important;
  font-size: 0.7rem !important;
  padding: 0 8px !important;
}

.v-btn--small {
  height: 28px !important;
  min-width: 56px !important;
  font-size: 0.75rem !important;
  padding: 0 12px !important;
}

.v-btn-toggle >>> .v-btn {
  min-width: 32px !important;
  height: 24px !important;
  font-size: 0.7rem !important;
  padding: 0 6px !important;
}

/* Compact Pagination */
.compact-pagination >>> .v-pagination__item {
  height: 28px !important;
  min-width: 32px !important;
  width: auto !important;
  padding: 0 6px !important;
  font-size: 0.75rem !important;
  margin: 0 1px !important;
}

.compact-pagination >>> .v-pagination__navigation {
  height: 28px !important;
  width: 28px !important;
  min-width: 28px !important;
}

.compact-input >>> .v-input__control .v-input__slot {
  min-height: 24px !important;
}

.compact-input >>> .v-input__control .v-input__slot input {
  padding: 2px 6px !important;
  font-size: 0.7rem !important;
}

/* Compact Chip Styling */
.v-chip {
  font-weight: 500;
  letter-spacing: 0.1px;
  font-size: 0.7rem !important;
  height: 20px !important;
}

.v-chip--x-small {
  height: 18px !important;
  font-size: 0.65rem !important;
}

.v-chip .v-icon {
  font-size: 12px !important;
}

/* Compact Avatar Styling */
.v-avatar {
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

/* Progress Enhancements */
.v-progress-circular {
  filter: drop-shadow(0 2px 4px rgba(25, 118, 210, 0.3));
}

/* Responsive Design */
@media (max-width: 768px) {
  .enhanced-table >>> tbody td {
    padding: 4px 4px !important;
    font-size: 0.7rem !important;
  }

  .enhanced-table >>> .v-data-table-header th {
    padding: 6px 4px !important;
    font-size: 0.7rem !important;
  }

  .v-card-title {
    padding: 8px 12px 4px !important;
  }

  .v-card-text {
    padding: 4px 12px 8px !important;
  }

  .v-card-actions {
    padding: 4px 12px !important;
  }

  .v-card-subtitle {
    padding: 4px 12px !important;
  }
}

/* No spin arrows for number input */
.no-spin-arrows >>> input[type="number"]::-webkit-outer-spin-button,
.no-spin-arrows >>> input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.no-spin-arrows >>> input[type="number"] {
  -moz-appearance: textfield;
}

/* Enhanced UI Styling */
.product-info-container {
  transition: all 0.2s ease;
}

.product-info-container:hover {
  transform: translateY(-1px);
}

.product-avatar {
  border: 2px solid #e0e0e0;
  transition: all 0.2s ease;
}

.product-avatar:hover {
  border-color: var(--v-primary-base);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.product-details {
  min-width: 0;
  flex: 1;
}

.order-info-container {
  transition: all 0.2s ease;
}

.order-info-container:hover {
  transform: translateY(-1px);
}

.order-details {
  min-width: 0;
  flex: 1;
}

.quantity-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.quantity-chip {
  font-weight: 600;
  letter-spacing: 0.5px;
}

.price-container {
  text-align: center;
}

.date-container {
  text-align: center;
}

.status-container {
  display: flex;
  justify-content: center;
}

.status-chip {
  font-weight: 600;
  min-width: 80px;
}

.actions-container {
  gap: 4px;
}

.action-btn {
  transition: all 0.2s ease;
  min-width: 32px;
}

.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

/* Dialog Styling */
.detail-section {
  margin-bottom: 1rem;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  padding: 0.25rem 0;
  border-bottom: 1px solid #f0f0f0;
}

.detail-label {
  font-weight: 500;
  color: #666;
  min-width: 120px;
}

.detail-value {
  font-weight: 600;
  color: #333;
  text-align: right;
}

.stat-card {
  text-align: center;
  padding: 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  transition: all 0.2s ease;
}

.stat-card:hover {
  border-color: var(--v-primary-base);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.stat-value {
  font-size: 1.25rem;
  font-weight: bold;
  color: var(--v-primary-base);
  margin-bottom: 0.25rem;
}

.stat-label {
  font-size: 0.75rem;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.item-info-card {
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
}

.item-info-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Animation Effects */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.enhanced-table >>> tbody tr {
  animation: fadeInUp 0.3s ease;
}

.enhanced-table >>> tbody tr:nth-child(even) {
  animation-delay: 0.05s;
}

.enhanced-table >>> tbody tr:nth-child(odd) {
  animation-delay: 0.1s;
}
</style>